import { useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { WatchlistSubNav } from '@/components/watchlist/SubNav';
import { CandidateScoreResult } from '@/components/watchlist/CandidateScoreResult';
import { GhostButton } from '@/components/watchlist/Modal';
import { wl, classificationColor } from '@/components/watchlist/theme';
import type { VipDetectedCandidate, ThreatIndicator, ImpersonationClassification } from '@/types/watchlist';

type Row =
  | { kind: 'vip'; id: string; profileId: string; subjectName: string; platform: string; handle: string; classification: ImpersonationClassification; risk: number; candidate: VipDetectedCandidate }
  | { kind: 'brand_logo'; id: string; profileId: string; subjectName: string; platform: string; handle: string; risk: number; threat: ThreatIndicator };

const FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'scam_impersonation', label: 'Scam impersonation' },
  { key: 'impersonation', label: 'Impersonation' },
  { key: 'declared_fan', label: 'Declared fan' },
  { key: 'official', label: 'Official' },
  { key: 'brand_logo', label: 'Logo reuse' },
];

export default function ImpersonationPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<Row | null>(null);
  const [creatingCase, setCreatingCase] = useState(false);

  const vipProfilesQuery = useQuery({ queryKey: ['watchlist-vip-profiles'], queryFn: () => watchlistApi.listVipProfiles() });
  const brandProfilesQuery = useQuery({ queryKey: ['watchlist-brand-profiles'], queryFn: () => watchlistApi.listBrandProfiles() });
  const vipProfiles = vipProfilesQuery.data?.vip_profiles ?? [];
  const brandProfiles = brandProfilesQuery.data?.brand_profiles ?? [];

  // Small-N N+1 fan-out across the user's own profiles — same tradeoff
  // already established for brands.tsx's stat cards: a new aggregate
  // backend endpoint isn't worth it for a handful of profiles per tenant.
  const vipCandidateQueries = useQueries({
    queries: vipProfiles.map((p) => ({
      queryKey: ['watchlist-vip-detected-candidates', p.id, false],
      queryFn: () => watchlistApi.listVipDetectedCandidates(p.id, false, 100),
      enabled: vipProfiles.length > 0,
    })),
  });
  const brandThreatQueries = useQueries({
    queries: brandProfiles.map((p) => ({
      queryKey: ['watchlist-brand-threats', p.id],
      queryFn: () => watchlistApi.listBrandProtectionThreats(p.id, 100),
      enabled: brandProfiles.length > 0,
    })),
  });

  const loading = vipProfilesQuery.isLoading || brandProfilesQuery.isLoading || vipCandidateQueries.some((q) => q.isLoading) || brandThreatQueries.some((q) => q.isLoading);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    vipProfiles.forEach((p, i) => {
      const candidates = vipCandidateQueries[i]?.data?.candidates ?? [];
      candidates
        .filter((c) => c.classification !== 'unrelated')
        .forEach((c) => out.push({
          kind: 'vip', id: c.id, profileId: p.id, subjectName: p.name,
          platform: c.platform, handle: c.candidate_handle,
          classification: (c.overridden_classification as ImpersonationClassification) ?? c.classification,
          risk: c.risk_score, candidate: c,
        }));
    });
    brandProfiles.forEach((p, i) => {
      const threats = brandThreatQueries[i]?.data?.threats ?? [];
      threats
        .filter((t) => (t.metadata as Record<string, unknown>)?.pattern_name === 'logo_reuse')
        .forEach((t, idx) => {
          const meta = t.metadata as Record<string, unknown>;
          out.push({
            kind: 'brand_logo', id: `${p.id}:${idx}`, profileId: p.id, subjectName: p.name,
            platform: t.source_platform, handle: (meta.matched_handle as string) ?? '—',
            risk: typeof meta.match_score === 'number' ? meta.match_score : t.confidence,
            threat: t,
          });
        });
    });
    return out.sort((a, b) => b.risk - a.risk);
  }, [vipProfiles, brandProfiles, vipCandidateQueries, brandThreatQueries]);

  const filtered = filter === 'all' ? rows : filter === 'brand_logo' ? rows.filter((r) => r.kind === 'brand_logo') : rows.filter((r) => r.kind === 'vip' && r.classification === filter);

  async function openTakedownCase(row: Row) {
    setCreatingCase(true);
    try {
      await watchlistApi.createTakedownCase({
        title: `${row.kind === 'vip' ? 'Impersonation' : 'Logo reuse'} — @${row.handle} on ${row.platform}`,
        profile_type: row.kind === 'vip' ? 'vip' : 'brand',
        profile_id: row.profileId,
        target_platform: row.platform,
        target_handle: row.handle !== '—' ? row.handle : undefined,
        classification: row.kind === 'vip' ? row.classification : 'logo_reuse',
        severity: row.kind === 'vip' ? row.candidate.severity : 'high',
        status: 'new',
        evidence_attachments: [],
        jurisdiction_compliance_refs: [],
      });
      qc.invalidateQueries({ queryKey: ['watchlist-takedown-cases'] });
      alert('Takedown case created — refine details on the Takedowns tab.');
    } finally {
      setCreatingCase(false);
    }
  }

  return (
    <div style={{ background: wl.bg, minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WatchlistSubNav />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 style={{ color: wl.text, fontWeight: 800, fontSize: 16 }}>Impersonation</h1>
          <p style={{ color: wl.textMuted, fontSize: 12, marginTop: 2 }}>Every candidate across your VIP profiles, plus brand logo-reuse — sorted by risk</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className="px-3 py-1.5 rounded-full font-semibold"
            style={{
              background: filter === f.key ? wl.greenWash : 'transparent',
              color: filter === f.key ? wl.green : wl.textMuted,
              border: `1px solid ${filter === f.key ? wl.green + '50' : wl.border}`,
              fontSize: 11.5, fontFamily: wl.font,
            }}
          >
            {f.label} {f.key !== 'all' && `· ${f.key === 'brand_logo' ? rows.filter((r) => r.kind === 'brand_logo').length : rows.filter((r) => r.kind === 'vip' && r.classification === f.key).length}`}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader2 className="animate-spin" size={22} style={{ color: wl.green }} />
      ) : filtered.length === 0 ? (
        <p style={{ color: wl.textFaint, fontSize: 13 }}>Nothing here yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
          <div className="flex flex-col gap-2" style={{ maxHeight: 640, overflowY: 'auto' }}>
            {filtered.map((row) => (
              <button
                key={row.id}
                onClick={() => setSelected(row)}
                className="text-left flex items-center justify-between px-3.5 py-3"
                style={{
                  background: selected?.id === row.id ? wl.greenWash : wl.surface,
                  border: `1px solid ${selected?.id === row.id ? wl.green + '50' : wl.border}`,
                  borderRadius: 8,
                }}
              >
                <div className="min-w-0">
                  <p style={{ color: wl.text, fontSize: 12.5, fontWeight: 600 }}>@{row.handle}</p>
                  <p style={{ color: wl.textMuted, fontSize: 10.5, marginTop: 2 }}>targets {row.subjectName} · {row.platform}</p>
                  <span
                    className="inline-block mt-1 px-1.5 py-0.5 rounded font-bold"
                    style={{ background: `${row.kind === 'brand_logo' ? wl.info : classificationColor(row.classification)}18`, color: row.kind === 'brand_logo' ? wl.info : classificationColor(row.classification), fontSize: 9.5 }}
                  >
                    {row.kind === 'brand_logo' ? 'LOGO REUSE' : row.classification.toUpperCase().replace('_', ' ')}
                  </span>
                </div>
                <span style={{ color: wl.text, fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{(row.risk * 100).toFixed(0)}</span>
              </button>
            ))}
          </div>

          <div>
            {!selected ? (
              <p style={{ color: wl.textFaint, fontSize: 12.5, marginTop: 24 }}>Select a candidate on the left.</p>
            ) : selected.kind === 'vip' ? (
              <div className="flex flex-col gap-3">
                <CandidateScoreResult
                  score={selected.candidate}
                  override={{ profileId: selected.profileId, candidateId: selected.id, onOverridden: () => qc.invalidateQueries({ queryKey: ['watchlist-vip-detected-candidates'] }) }}
                />
                <div className="flex justify-end">
                  <GhostButton onClick={() => openTakedownCase(selected)}>{creatingCase ? 'Creating…' : 'Open takedown case'}</GhostButton>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }}>
                  <div className="flex items-start gap-2">
                    <ShieldAlert size={14} style={{ color: wl.critical, marginTop: 1 }} />
                    <div>
                      <p style={{ color: wl.text, fontSize: 13 }}>{selected.threat.indicators}</p>
                      <p style={{ color: wl.textMuted, fontSize: 11, marginTop: 4 }}>
                        {selected.threat.source_platform} · {new Date(selected.threat.timestamp).toLocaleString()} · match {(selected.risk * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <GhostButton onClick={() => openTakedownCase(selected)}>{creatingCase ? 'Creating…' : 'Open takedown case'}</GhostButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
